import { useState } from "react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import ChatWidget from "../components/site/ChatWidget";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { api, CONTACT } from "../lib/api";
import { MapPin, Phone, Mail, Clock, Loader2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — we'll get back to you within a few hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="pt-32 pb-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-14">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary mb-3">Contact</div>
            <h1 className="font-display text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Say hello. We answer within 3 rings.
            </h1>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Have a question about your upcoming move? Prefer WhatsApp or a phone call? We&apos;re open all seven days.
            </p>
            <ul className="mt-10 space-y-5">
              <Info icon={MapPin} title="Head office" text={CONTACT.address} />
              <Info icon={Phone} title="Phone / WhatsApp" text={CONTACT.phone} href={`tel:${CONTACT.phoneRaw}`} />
              <Info icon={Mail} title="Email" text={CONTACT.email} />
              <Info icon={Clock} title="Hours" text={CONTACT.hours} />
            </ul>
          </div>

          <form
            onSubmit={submit}
            className="rounded-2xl border border-border bg-background p-8 md:p-10 shadow-sm"
            data-testid="contact-form"
          >
            <h2 className="font-display text-2xl mb-6">Send us a message</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full name">
                <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Email">
                <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label="Phone">
                <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>
              <Field label="Subject">
                <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              </Field>
              <Field label="Message" className="sm:col-span-2">
                <Textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </Field>
            </div>
            <Button
              data-testid="contact-submit"
              disabled={loading}
              type="submit"
              className="mt-6 h-12 w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Send message"}
            </Button>
          </form>
        </div>
      </section>
      <Footer />
      <ChatWidget />
    </div>
  );
}

function Info({ icon: Icon, title, text, href }) {
  const content = (
    <div className="flex gap-4">
      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{title}</div>
        <div className="mt-1">{text}</div>
      </div>
    </div>
  );
  return href ? <li><a href={href} className="hover:text-primary">{content}</a></li> : <li>{content}</li>;
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">{label}</Label>
      {children}
    </div>
  );
}
